// Yandex Metrika Analytics Helper
// Counter ID: 106548895

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ym?: (...args: any[]) => void;
    dataLayer: unknown[];
  }
}

const COUNTER_ID = 106548895;

// Queue for events fired before ym is ready
const pendingGoals: Array<{ action: string; params?: Record<string, unknown> }> = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

function isYmReady(): boolean {
  if (typeof window === 'undefined' || !window.ym) return false;
  // Check that ym is the real function (not just the stub)
  // The stub has an `.a` property; the real function from tag.js does not
  // However, after init the stub may still be there while tag.js processes
  // So we also check if the counter object exists
  return typeof window.ym === 'function';
}

function flushPendingGoals() {
  if (!isYmReady()) return;
  while (pendingGoals.length > 0) {
    const goal = pendingGoals.shift()!;
    try {
      window.ym!(COUNTER_ID, 'reachGoal', goal.action, goal.params);
      console.log(`[YM] Goal flushed: ${goal.action}`, goal.params);
    } catch (e) {
      console.error('[YM] Error flushing goal:', e);
    }
  }
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

/**
 * Track a custom goal/event in Yandex.Metrika
 * Queues the event if ym is not yet ready and retries for up to 30s
 */
export const trackEvent = (action: string, params?: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return;

  if (isYmReady()) {
    try {
      window.ym!(COUNTER_ID, 'reachGoal', action, params);
      console.log(`[YM] Goal tracked: ${action}`, params);
    } catch (error) {
      console.error('[YM] Error tracking goal:', error);
    }
  } else {
    // Queue and start polling for ym readiness
    console.log(`[YM] Queued goal (ym not ready): ${action}`, params);
    pendingGoals.push({ action, params });
    if (!flushTimer) {
      let attempts = 0;
      flushTimer = setInterval(() => {
        attempts++;
        if (isYmReady()) {
          flushPendingGoals();
        } else if (attempts >= 30) {
          console.warn('[YM] Gave up waiting for ym after 30s, dropping queued goals');
          pendingGoals.length = 0;
          if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
        }
      }, 1000);
    }
  }
};

/**
 * Track a page hit (for SPA navigation)
 * @param url - The URL to track
 * @param options - Optional hit options
 */
export const trackHit = (url: string, options?: { title?: string; referer?: string }): void => {
  if (typeof window !== 'undefined' && isYmReady()) {
    try {
      window.ym!(COUNTER_ID, 'hit', url, options);
      console.log(`[YM] Page hit tracked: ${url}`);
    } catch (error) {
      console.error('[YM] Error tracking hit:', error);
    }
  }
};

/**
 * Push ecommerce data to dataLayer
 * @param data - Ecommerce event data
 */
export const pushEcommerce = (data: Record<string, unknown>): void => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
    console.log('[YM] Ecommerce data pushed:', data);
  }
};

/**
 * Track add to cart ecommerce event
 */
export const trackAddToCart = (product: {
  id: string;
  name: string;
  price: number;
  quantity?: number;
}): void => {
  pushEcommerce({
    ecommerce: {
      add: {
        products: [{
          id: product.id,
          name: product.name,
          price: product.price,
          quantity: product.quantity || 1,
        }]
      }
    }
  });
  
  // Also track as a goal
  trackEvent('add_to_cart', { productId: product.id, productName: product.name });
};

/**
 * Track PDF download event
 */
export const trackDownloadPDF = (productName?: string): void => {
  trackEvent('download_pdf', productName ? { productName } : undefined);
};

// Predefined goal actions for consistency
export const GOALS = {
  PRODUCT_VIEW: 'product_view',
  ADD_TO_CART: 'add_to_cart',
  CHECKOUT_START: 'checkout_start',
  BEGIN_CHECKOUT: 'begin_checkout',
  PURCHASE: 'purchase',
  DOWNLOAD_PDF: 'download_pdf',
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
  CONTACT_FORM: 'contact_form',
  CONTACT_FORM_SUBMIT: 'contact_form_submit',
  PHONE_CLICK: 'phone_click',
  ACCOUNT_CREATED_POST_PURCHASE: 'ACCOUNT_CREATED_POST_PURCHASE',
  REGISTRATION_START: 'REGISTRATION_START',
  REGISTRATION_SUCCESS: 'REGISTRATION_SUCCESS',
} as const;

/**
 * Track ecommerce purchase event via dataLayer
 */
export const trackEcommercePurchase = (order: {
  id: string;
  total: number;
  products: Array<{ id: string; name: string; price: number; quantity: number }>;
}): void => {
  pushEcommerce({
    ecommerce: {
      purchase: {
        actionField: {
          id: order.id,
          revenue: order.total,
        },
        products: order.products,
      },
    },
  });
};

/**
 * Track phone number click
 */
export const trackPhoneClick = (phoneNumber: string): void => {
  trackEvent(GOALS.PHONE_CLICK, { phone: phoneNumber });
};

/**
 * Track contact form submission
 */
export const trackContactFormSubmit = (): void => {
  trackEvent(GOALS.CONTACT_FORM_SUBMIT);
};

/**
 * Send userParams to Yandex.Metrika for user segmentation
 * Safe to call multiple times — Metrika merges params
 */
export const trackUserParams = (params: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return;

  if (isYmReady()) {
    try {
      window.ym!(COUNTER_ID, 'userParams', params);
      console.log('[YM] userParams sent:', params);
    } catch (error) {
      console.error('[YM] Error sending userParams:', error);
    }
  } else {
    // Queue and retry similar to goals
    const tryCount = { n: 0 };
    const interval = setInterval(() => {
      tryCount.n++;
      if (isYmReady()) {
        clearInterval(interval);
        try {
          window.ym!(COUNTER_ID, 'userParams', params);
          console.log('[YM] userParams sent (deferred):', params);
        } catch (e) {
          console.error('[YM] Error sending deferred userParams:', e);
        }
      } else if (tryCount.n >= 30) {
        clearInterval(interval);
        console.warn('[YM] Gave up sending userParams after 30s');
      }
    }, 1000);
  }
};

export type GoalAction = typeof GOALS[keyof typeof GOALS];
